import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useHistory } from 'react-router';
import { ConfirmationModal, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useStoreActions } from '~/contexts';
import { useDraftProposal } from '~/hooks/reactQuery/mutations/useDraftProposal';
import { useMutationState } from '~/hooks/reactQuery/useMutationState';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
export const WithdrawToEditProposalTestIds = getTestIds('WithdrawToEditProposalTestIds');
export function useWithdrawToEditProposal() {
    const overlayContext = useOverlayContext();
    const { api } = useStoreActions();
    const history = useHistory();
    const { EDIT_PROPOSAL_IN_PLACE } = useFeatureFlags();
    const { t } = useLocaleKeys('agreementStudio.editInPlace');
    const { draftProposal } = useDraftProposal({
        onSuccess: (newDraftId)=>history.push(routes.draft(newDraftId))
    });
    const { mutateAsync: withdrawForEditing } = useMutationState({
        mutationFn: async (param)=>{
            let { id } = param;
            await api.relationship.withdrawForEditing(id);
        },
        withPageLoader: true,
        mutationName: 'withdraw-for-editing',
        toasts: {
            errorMessage: t.withdrawToEditErrorToast(),
            errorTestId: WithdrawToEditProposalTestIds.ErrorToast()
        }
    });
    async function showWithdrawConfirmation(action, modalProps) {
        const confirmed = await overlayContext.showModal({
            biName: 'withdraw_to_edit_proposal_confirm',
            closeOnClickOutside: true,
            shouldBlurBackground: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, _object_spread_props(_object_spread({
                    skin: "danger"
                }, modalProps), {
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false)
                }));
            }
        });
        if (confirmed) {
            try {
                await action();
            } catch (unused) {
            // errors handled by useMutationState onError
            }
        }
    }
    return {
        withdrawToEditProposalByIdWithConfirmation: async (id, draftId)=>{
            if (EDIT_PROPOSAL_IN_PLACE) {
                return showWithdrawConfirmation(async ()=>{
                    await withdrawForEditing({
                        id
                    });
                    history.push(routes.draft(draftId));
                }, {
                    bi: {
                        primaryButton: {
                            name: 'withdraw_to_edit_proposal_confirm'
                        },
                        secondaryButton: {
                            name: 'withdraw_to_edit_proposal_cancel'
                        }
                    },
                    title: t.withdrawToEditConfirmationTitle(),
                    content: t.withdrawToEditConfirmationContent(),
                    primaryButtonLabel: t.withdrawToEditConfirmationPrimaryButton(),
                    secondaryButtonLabel: t.withdrawToEditSecondaryButton()
                });
            }
            return showWithdrawConfirmation(()=>{
                draftProposal({
                    relationshipId: id,
                    draftId
                });
            }, {
                bi: {
                    primaryButton: {
                        name: 'withdraw_to_edit_legacy_proposal_confirm'
                    },
                    secondaryButton: {
                        name: 'withdraw_to_edit_legacy_proposal_cancel'
                    }
                },
                title: t.withdrawToEditLegacyConfirmationTitle(),
                content: t.withdrawToEditLegacyConfirmationContent(),
                primaryButtonLabel: t.withdrawToEditLegacyConfirmationPrimaryButton(),
                secondaryButtonLabel: t.withdrawToEditSecondaryButton()
            });
        },
        withdrawToEditProposalById: async (id, opts)=>{
            if (!EDIT_PROPOSAL_IN_PLACE) {
                throw new Error('Cannot withdraw proposal for editing without EDIT_PROPOSAL_IN_PLACE');
            }
            await withdrawForEditing({
                id
            });
            return opts.draftId;
        }
    };
}
